import type { Pinia } from 'pinia';
import { healthConnectService } from './healthConnectService';
import { useWeightStore, type WeightEntry } from '@/stores/weightStore';
import { getDiaryDaysInRange, type DiaryMeal } from '@/services/diaryDays';
import { activeMeals } from '@/utils/diaryDayTotals';
import { getSession } from '@/services/authStore';
import { formatDateInput } from '@/utils/dateTime';
import type { NutritionRecord as HealthConnectNutritionRecord } from '../plugins/health-connect/src/definitions';
import { captureDeveloperLog } from './developerLogging';

const HC_SOURCE = 'healthConnect';
// How far back to look on the first weight sync (when no watermark exists yet).
const DEFAULT_LOOKBACK_DAYS = 90;
// Nutrition is re-pushed over a rolling window so edits to recent days
// propagate. Writes are idempotent (keyed by meal id) so overlap is harmless.
const NUTRITION_WINDOW_DAYS = 7;
// Health Connect meal-type constants: 1=Breakfast 2=Lunch 3=Dinner 4=Snack.
const MEAL_TYPE_BY_SLOT: Record<number, number> = { 0: 1, 1: 2, 2: 3, 3: 4 };

let inFlight: Promise<void> | null = null;
let nutritionInFlight: Promise<void> | null = null;

const daysAgo = (days: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
};

const isAppAuthored = (entry: WeightEntry): boolean => entry.source !== HC_SOURCE;

/**
 * Pulls weight records from Health Connect into the app's weight history.
 *
 * Invariants enforced:
 *  - At most one weight entry per local day.
 *  - An entry added in the app (any source other than 'healthConnect') is
 *    authoritative: Health Connect never overwrites it, and any stale
 *    imported entry for that day is removed.
 *  - A day with only an imported entry is kept in sync with Health Connect.
 *
 * Safe to call repeatedly (e.g. on every app resume): the day-level dedup makes
 * overlapping import windows idempotent.
 */
export async function syncHealthConnectWeightIn(pinia: Pinia): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const status = healthConnectService.getStatus();
    const config = healthConnectService.getConfig();

    if (!config.enabled || !status.available || !status.readWeight) {
      return;
    }

    const since = config.lastWeightSync
      ? new Date(config.lastWeightSync)
      : daysAgo(DEFAULT_LOOKBACK_DAYS);

    const imported = await healthConnectService.readWeight(since);

    if (imported.length > 0) {
      const weightStore = useWeightStore(pinia);
      await weightStore.ensureLoaded();

      // Collapse Health Connect records to one-per-local-day; latest wins.
      const byDay = new Map<string, { weightKg: number; measuredAtUtc: string }>();
      for (const rec of imported) {
        if (rec.weightKg == null || !rec.measuredAtUtc) continue;
        const day = formatDateInput(new Date(rec.measuredAtUtc));
        const prev = byDay.get(day);
        if (!prev || rec.measuredAtUtc > prev.measuredAtUtc) {
          byDay.set(day, { weightKg: rec.weightKg, measuredAtUtc: rec.measuredAtUtc });
        }
      }

      for (const [day, rec] of byDay) {
        const existing = weightStore.getEntriesInRange(day, day);
        const appAuthored = existing.find(isAppAuthored);
        const hcExisting = existing.find((e) => e.source === HC_SOURCE);

        if (appAuthored) {
          // App entry wins. Drop any stale imported duplicate for the day.
          if (hcExisting) {
            await weightStore.deleteEntry(hcExisting.id);
          }
          continue;
        }

        if (hcExisting) {
          if (
            hcExisting.weightKg !== rec.weightKg ||
            hcExisting.measuredAtUtc !== rec.measuredAtUtc
          ) {
            await weightStore.updateEntry(hcExisting.id, {
              weightKg: rec.weightKg,
              measuredAtUtc: rec.measuredAtUtc,
              localDate: day,
              source: HC_SOURCE,
            });
          }
        } else {
          await weightStore.createEntry({
            weightKg: rec.weightKg,
            measuredAtUtc: rec.measuredAtUtc,
            localDate: day,
            source: HC_SOURCE,
          });
        }
      }
    }

    await healthConnectService.updateConfig({ lastWeightSync: new Date().toISOString() });
    captureDeveloperLog('info', '[HealthConnect] weight sync complete, imported', imported.length, 'record(s)');
  })();

  try {
    await inFlight;
  } catch (e) {
    captureDeveloperLog('error', '[HealthConnect] weight sync failed', e);
  } finally {
    inFlight = null;
  }
}

/** Sum a meal's food (non-exercise) entries into a macro total. */
const mealFoodTotals = (meal: DiaryMeal) => {
  const total = { calories: 0, protein: 0, carbs: 0, fat: 0 };
  for (const entry of meal.entries) {
    if (entry.source.type === 'exercise') continue;
    total.calories += entry.nutritionSnapshot.calories ?? 0;
    total.protein += entry.nutritionSnapshot.protein ?? 0;
    total.carbs += entry.nutritionSnapshot.carbs ?? 0;
    total.fat += entry.nutritionSnapshot.fat ?? 0;
  }
  return total;
};

/**
 * Pushes logged meals to Health Connect as nutrition records over a rolling
 * recent window. One record per meal, keyed by a stable `clientRecordId`
 * (`meal:<mealId>`) so re-runs upsert the same record instead of duplicating.
 *
 * Note: this only creates/updates records. A meal deleted or emptied in the app
 * is not removed from Health Connect (that needs delete-by-clientRecordId).
 */
export async function syncHealthConnectNutritionOut(): Promise<void> {
  if (nutritionInFlight) return nutritionInFlight;

  nutritionInFlight = (async () => {
    const status = healthConnectService.getStatus();
    const config = healthConnectService.getConfig();

    if (!config.enabled || !status.available || !status.writeNutrition) {
      return;
    }

    const session = await getSession();
    const userId = session?.user.id;
    if (!userId) return;

    const to = formatDateInput();
    const from = formatDateInput(daysAgo(NUTRITION_WINDOW_DAYS - 1));
    const days = await getDiaryDaysInRange(userId, from, to);

    const records: HealthConnectNutritionRecord[] = [];
    for (const day of days) {
      let mealIndex = 0;
      for (const meal of activeMeals(day.body)) {
        const totals = mealFoodTotals(meal);
        if (totals.calories <= 0 && totals.protein <= 0 && totals.carbs <= 0 && totals.fat <= 0) {
          continue;
        }

        // Stagger meals across the day so records don't collapse onto one instant.
        const start = new Date(`${day.date}T12:00:00`);
        start.setMinutes(start.getMinutes() + mealIndex * 30);
        const end = new Date(start.getTime() + 30 * 60 * 1000);
        mealIndex += 1;

        records.push({
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          name: meal.custom?.title,
          mealType: meal.mealId != null ? (MEAL_TYPE_BY_SLOT[meal.mealId] ?? 0) : 0,
          energy: { value: totals.calories, unit: 'kilocalories' },
          protein: { value: totals.protein, unit: 'grams' },
          totalCarbohydrate: { value: totals.carbs, unit: 'grams' },
          totalFat: { value: totals.fat, unit: 'grams' },
          metadata: { clientRecordId: `meal:${meal.id}` },
        });
      }
    }

    const ok = await healthConnectService.writeNutritionRecords(records);
    if (ok) {
      await healthConnectService.updateConfig({ lastNutritionSync: new Date().toISOString() });
    }
    captureDeveloperLog('info', '[HealthConnect] nutrition sync complete, pushed', records.length, 'record(s)');
  })();

  try {
    await nutritionInFlight;
  } catch (e) {
    captureDeveloperLog('error', '[HealthConnect] nutrition sync failed', e);
  } finally {
    nutritionInFlight = null;
  }
}
